import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SpeedIcon from '@mui/icons-material/Speed';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { VehicleMakeLogo } from '@/components/common/VehicleMakeLogo';
import { InlineVehicleFields } from '@/components/vehicles/InlineVehicleFields';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { InlineMileageEdit } from './InlineMileageEdit';
import { VehicleChangePanel } from './VehicleChangePanel';

interface VehicleInfoTabProps {
  workOrder: WorkOrderDetail;
  mileageSaving: boolean;
  onMileageSave: (mileage: number) => void | Promise<void>;
  /** Opens the vehicle card (#3400); the plate and the menu both lead there. */
  onOpenVehicleCard?: () => void;
}

export function VehicleInfoTab({
  workOrder,
  mileageSaving,
  onMileageSave,
  onOpenVehicleCard,
}: VehicleInfoTabProps) {
  const { t } = useTranslation('workorders');
  // "Change vehicle" sits behind an overflow menu like "Change customer" on
  // the customer card (#552): rare, and it rewrites what the order is for. The
  // label must differ from the header's "More options" (print menu), which the
  // e2e test addresses by accessible name.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [isChangingVehicle, setIsChangingVehicle] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <VehicleMakeLogo make={workOrder.vehicle.make} size={48} />
        <Box sx={{ flex: 1 }}>
          <RegistrationPlate
            registrationNumber={workOrder.vehicle.registrationNumber}
            sx={{ fontSize: '1rem' }}
            copyable
            {...(onOpenVehicleCard ? { onClick: onOpenVehicleCard, openLabel: t('vehicleTab.openVehicleCard') } : {})}
          />
        </Box>
        <Tooltip title={t('detail.vehicleActions')}>
          <IconButton
            size="small"
            onClick={(event) => setActionsAnchor(event.currentTarget)}
            aria-label={t('detail.vehicleActions')}
            aria-haspopup="menu"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={() => setActionsAnchor(null)}
        >
          {onOpenVehicleCard ? (
            <MenuItem
              onClick={() => {
                setActionsAnchor(null);
                onOpenVehicleCard();
              }}
            >
              <DirectionsCarIcon fontSize="small" sx={{ mr: 1 }} />
              {t('vehicleTab.openVehicleCard')}
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={() => {
              setActionsAnchor(null);
              setIsChangingVehicle(true);
            }}
          >
            <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
            {t('vehicleTab.changeVehicle')}
          </MenuItem>
        </Menu>
      </Box>
      {isChangingVehicle && (
        <VehicleChangePanel workOrder={workOrder} onClose={() => setIsChangingVehicle(false)} />
      )}
      <InlineVehicleFields
        vehicle={{
          id: workOrder.vehicle.id,
          registrationNumber: workOrder.vehicle.registrationNumber,
          make: workOrder.vehicle.make,
          model: workOrder.vehicle.model,
          year: workOrder.vehicle.year,
          // Always present so the row shows (and can be filled in) here (#3383).
          vin: workOrder.vehicle.vin ?? null,
        }}
      />
      <List dense disablePadding>
        <ListItem disablePadding sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SpeedIcon fontSize="small" color="action" />
          </ListItemIcon>
          <InlineMileageEdit
            value={workOrder.vehicle.mileage}
            onSave={(km) => { void onMileageSave(km); }}
            saving={mileageSaving}
          />
        </ListItem>
      </List>
    </>
  );
}
